import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Cloud,
  FileKey2,
  KeyRound,
  Landmark,
  Loader2,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  getMeControllerObterQueryKey,
  useMeControllerConcluirSafeId,
  useMeControllerEnviarCertificadoA1,
  useMeControllerEsquecerSenhaDoCertificado,
  useMeControllerGuardarSenhaDoCertificado,
  useMeControllerIniciarSafeId,
  useMeControllerIniciarSerproId,
} from '@/api/generated/me/me';
import {
  MeAssinaturaTipo,
  MeAutorizacaoSafeIdSituacao,
  MeCertificadoDigitalTipo,
  type MeResponse,
} from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { aoResponder } from '@/features/perfil/lib/aoResponder';
import { irParaOSerpro } from '@/features/perfil/lib/irParaOSerpro';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { InputSenha } from '@/shared/ui/input-senha';
import { toast } from '@/shared/ui/sonner';

/** Só o A1 é arquivo. SafeID e SerproID ficam no HSM do PSC, e o A3 em token não sai de lá. */
const EXTENSOES_DO_A1 = '.pfx,.p12';

/** De quanto em quanto tempo a tela pergunta se o médico já aprovou no celular. */
const INTERVALO_DA_ESPERA_MS = 5_000;

/**
 * As duas opções de senha, com o custo de cada uma dito em voz alta. A escolha
 * é do médico — o sistema não decide por ele o que é praticidade aceitável.
 * Valem igual para o A1 e para o SafeID. O SerproID não tem senha: ele assina
 * por uma sessão aprovada no app, e por isso não passa por aqui.
 */
const MODOS_DE_SENHA = [
  {
    guardar: false,
    titulo: 'Pedir a senha a cada assinatura',
    descricao:
      'Mais seguro: sem a senha, ninguém assina em seu nome — nem quem conseguir entrar na sua conta.',
  },
  {
    guardar: true,
    titulo: 'Guardar a senha e assinar sem perguntar',
    descricao:
      'Mais prático. A senha fica guardada criptografada, mas quem entrar na sua conta consegue assinar documentos com o seu certificado.',
  },
] as const;

const ORIGENS = [
  {
    tipo: MeCertificadoDigitalTipo.A1,
    titulo: 'Arquivo A1 (.pfx)',
    descricao: 'O certificado que você baixou da autoridade certificadora e guarda no computador.',
    icone: FileKey2,
  },
  {
    tipo: MeCertificadoDigitalTipo.SAFEID,
    titulo: 'SafeID (certificado em nuvem)',
    descricao: 'O A3 da Safeweb. A chave nunca sai do servidor deles; você autoriza uma vez pelo app.',
    icone: Cloud,
  },
  {
    tipo: MeCertificadoDigitalTipo.SERPROID,
    titulo: 'SerproID (certificado em nuvem do Serpro)',
    descricao:
      'O A3 do Serpro. Sem senha: você aprova no app SerproID e assina durante a sessão que escolher.',
    icone: Landmark,
  },
] as const;

type Origem = (typeof MeCertificadoDigitalTipo)[keyof typeof MeCertificadoDigitalTipo];

/**
 * Aba "Certificado" do cartão de assinatura: o certificado que ASSINA o PDF
 * (PAdES), por arquivo A1 ou por certificado em nuvem (SafeID e SerproID) — e,
 * no A1 e no SafeID, a escolha entre guardar a senha ou digitá-la a cada
 * assinatura, que dá para trocar depois.
 *
 * O certificado com só a parte pública (`.cer`) continua valendo para quem já
 * o enviou, mas não é mais oferecido: ele identifica o titular e não assina
 * nada, e a tela não deve induzir ninguém a achar o contrário.
 */
export function CertificadoDigital({ me }: { me: MeResponse }) {
  const queryClient = useQueryClient();
  const { assinatura } = me.perfil;
  const ehCertificado = assinatura.tipo === MeAssinaturaTipo.CERTIFICADO;
  const certificado = ehCertificado ? assinatura.certificado : null;
  const autorizacao = assinatura.autorizacaoSafeId;

  const enviarA1 = useMeControllerEnviarCertificadoA1();
  const iniciarSafeId = useMeControllerIniciarSafeId();
  const concluirSafeId = useMeControllerConcluirSafeId();
  const iniciarSerproId = useMeControllerIniciarSerproId();
  const guardar = useMeControllerGuardarSenhaDoCertificado();
  const esquecer = useMeControllerEsquecerSenhaDoCertificado();
  const ocupado =
    enviarA1.isPending ||
    iniciarSafeId.isPending ||
    concluirSafeId.isPending ||
    iniciarSerproId.isPending ||
    guardar.isPending ||
    esquecer.isPending;

  const entradaDeArquivo = useRef<HTMLInputElement>(null);
  const [origem, setOrigem] = useState<Origem>(certificado?.tipo ?? MeCertificadoDigitalTipo.A1);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [cpf, setCpf] = useState('');
  const [documentoDoSerproId, setDocumentoDoSerproId] = useState('');
  const [senha, setSenha] = useState('');
  const [guardarSenha, setGuardarSenha] = useState(false);
  const [senhaParaGuardar, setSenhaParaGuardar] = useState('');

  const esperandoAprovacao = autorizacao?.situacao === MeAutorizacaoSafeIdSituacao.AGUARDANDO;

  /**
   * ⚠️ Quem termina a autorização é a Safeweb, chamando o servidor quando o
   * médico aprova no celular — não há evento nenhum nesta aba. Sem esta espera
   * ativa, a tela ficaria em "aguardando" até alguém recarregar a página.
   */
  useEffect(() => {
    if (!esperandoAprovacao) return;

    const relogio = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: getMeControllerObterQueryKey() });
    }, INTERVALO_DA_ESPERA_MS);

    return () => clearInterval(relogio);
  }, [esperandoAprovacao, queryClient]);

  function enviarCertificadoA1() {
    if (!arquivo || !senha) return;

    enviarA1.mutate(
      { data: { arquivo, senha, guardarSenha: guardarSenha ? 'true' : 'false' } },
      {
        ...aoResponder(queryClient, 'Certificado enviado. Os próximos documentos saem assinados digitalmente.'),
        onSettled: () => setSenha(''),
      },
    );
    // O arquivo sai do formulário na hora; a senha, só depois da resposta — se
    // ela estiver errada, a pessoa corrige sem escolher o arquivo de novo.
    setArquivo(null);
  }

  return (
    <div className="space-y-6">
      {certificado && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-foreground">{assinatura.texto}</p>
              {assinatura.validoAte && (
                <p className="text-xs text-muted-foreground">{`Válido até ${formatarData(assinatura.validoAte)}`}</p>
              )}
              {certificado.emissor && (
                <p className="text-xs text-muted-foreground">{`Emitido por ${certificado.emissor}`}</p>
              )}
              <p className="text-xs text-muted-foreground">{rotuloDoTipo(certificado.tipo)}</p>
            </div>
            <Badge
              variant="outline"
              className="gap-1 border-emerald-600/40 text-emerald-700 dark:text-emerald-400"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Assina o PDF
            </Badge>
          </div>

          <div className="border-t border-border pt-3 space-y-3">
            {certificado.tipo === MeCertificadoDigitalTipo.SERPROID ? (
              <SessaoDoSerproId sessaoAte={certificado.sessaoAte} />
            ) : certificado.senhaGuardada ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-foreground">
                  Senha guardada — os documentos são assinados sem pedir a senha.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={ocupado}
                  onClick={() =>
                    esquecer.mutate(
                      undefined,
                      aoResponder(queryClient, 'A senha foi apagada. Cada assinatura vai pedi-la.'),
                    )
                  }
                >
                  Pedir a senha a cada assinatura
                </Button>
              </div>
            ) : (
              <form
                className="space-y-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!senhaParaGuardar) return;
                  guardar.mutate(
                    { data: { senha: senhaParaGuardar } },
                    {
                      ...aoResponder(queryClient, 'Senha guardada. Os documentos passam a ser assinados sem perguntar.'),
                      onSettled: () => setSenhaParaGuardar(''),
                    },
                  );
                }}
              >
                <p className="text-sm text-foreground">A senha é pedida a cada assinatura.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <InputSenha
                      aria-label="Senha do certificado para guardar"
                      className={CLASSE_CONTROLE}
                      placeholder="Senha do certificado"
                      autoComplete="off"
                      value={senhaParaGuardar}
                      onChange={(e) => setSenhaParaGuardar(e.target.value)}
                    />
                  </div>
                  <Button type="submit" variant="outline" className="gap-2" disabled={ocupado || !senhaParaGuardar}>
                    <KeyRound className="w-4 h-4" />
                    Guardar senha
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {ehCertificado && !certificado && (
        <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
          <p className="text-sm text-foreground">
            O certificado enviado antes tem só a parte pública: ele identifica você no documento, mas{' '}
            <strong className="font-medium">não assina o PDF</strong>. Configure abaixo o certificado que
            assina.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <p className="text-sm font-medium text-brand">
          {certificado ? 'Trocar o certificado' : 'Configurar o certificado que assina'}
        </p>

        <fieldset className="grid gap-2 sm:grid-cols-2">
          <legend className="sr-only">Origem do certificado</legend>
          {ORIGENS.map((opcao) => (
            <label
              key={opcao.tipo}
              className={cn(
                'flex gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                origem === opcao.tipo ? 'border-brand bg-brand/5' : 'border-border hover:border-brand/40',
              )}
            >
              <input
                type="radio"
                name="origem-do-certificado"
                className="mt-1 accent-brand"
                checked={origem === opcao.tipo}
                onChange={() => setOrigem(opcao.tipo)}
              />
              <span className="space-y-0.5">
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <opcao.icone className="w-4 h-4" />
                  {opcao.titulo}
                </span>
                <span className="block text-xs text-muted-foreground">{opcao.descricao}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {origem === MeCertificadoDigitalTipo.A1 ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                ref={entradaDeArquivo}
                type="file"
                accept={EXTENSOES_DO_A1}
                className="hidden"
                aria-label="Arquivo do certificado A1"
                onChange={(e) => {
                  setArquivo(e.target.files?.[0] ?? null);
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={ocupado}
                onClick={() => entradaDeArquivo.current?.click()}
              >
                <FileKey2 className="w-4 h-4" />
                Escolher arquivo .pfx
              </Button>
              <span className="text-sm text-muted-foreground truncate">
                {arquivo ? arquivo.name : 'Nenhum arquivo escolhido'}
              </span>
            </div>

            <Campo id="senha-do-certificado-a1" rotulo="Senha do certificado">
              <InputSenha
                id="senha-do-certificado-a1"
                className={CLASSE_CONTROLE}
                autoComplete="off"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            </Campo>

            <ModoDeSenha valor={guardarSenha} aoEscolher={setGuardarSenha} />

            <div className="flex justify-end">
              <Button
                type="button"
                className="gap-2"
                disabled={ocupado || !arquivo || !senha}
                onClick={enviarCertificadoA1}
              >
                {enviarA1.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Enviar certificado
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              O arquivo é guardado criptografado e os documentos passam a sair com assinatura digital,
              verificável em validar.iti.gov.br. Certificado A3 em token ou cartão não pode ser enviado por
              arquivo — nesse caso, use o SafeID ou o SerproID.
            </p>
          </>
        ) : origem === MeCertificadoDigitalTipo.SERPROID ? (
          <SerproId
            ocupado={ocupado}
            renovando={certificado?.tipo === MeCertificadoDigitalTipo.SERPROID}
            documento={documentoDoSerproId}
            aoTrocarDocumento={setDocumentoDoSerproId}
            aoAutorizar={() => {
              const documento = documentoDoSerproId.replace(/\D/g, '');

              iniciarSerproId.mutate(
                { data: documento ? { documento } : {} },
                {
                  // Não há `aoResponder` aqui: a resposta não é o `MeResponse`,
                  // é a URL do Serpro — e o que acontece depois é sair da tela.
                  onSuccess: ({ url }) => irParaOSerpro(url),
                  onError: (erro: unknown) =>
                    toast.error(
                      erro instanceof ApiError
                        ? erro.message
                        : 'Não foi possível iniciar a autorização no SerproID',
                    ),
                },
              );
            }}
          />
        ) : (
          <SafeId
            autorizacao={autorizacao}
            ocupado={ocupado}
            cpf={cpf}
            aoTrocarCpf={setCpf}
            senha={senha}
            aoTrocarSenha={setSenha}
            guardarSenha={guardarSenha}
            aoEscolherModo={setGuardarSenha}
            aoAutorizar={() =>
              iniciarSafeId.mutate(
                { data: { cpf: cpf.replace(/\D/g, '') } },
                aoResponder(queryClient, 'Autorize o uso do certificado no app SafeID.'),
              )
            }
            aoConcluir={() =>
              concluirSafeId.mutate(
                { data: { senha, guardarSenha } },
                {
                  ...aoResponder(
                    queryClient,
                    'SafeID configurado. Os próximos documentos saem assinados digitalmente.',
                  ),
                  onSettled: () => setSenha(''),
                },
              )
            }
          />
        )}
      </div>
    </div>
  );
}

function rotuloDoTipo(tipo: Origem): string {
  switch (tipo) {
    case MeCertificadoDigitalTipo.SAFEID:
      return 'SafeID — certificado em nuvem';
    case MeCertificadoDigitalTipo.SERPROID:
      return 'SerproID — certificado em nuvem';
    default:
      return 'Certificado A1 — arquivo';
  }
}

/**
 * O que o SerproID tem no lugar da senha: a SESSÃO que o médico aprovou no app.
 *
 * Vencida, o certificado continua cadastrado e a tela continua dizendo
 * "Assina o PDF" — mas assinar um documento falha até autorizar de novo. É por
 * isso que o aviso mora aqui, em cima do certificado, e não só na hora de
 * assinar: o médico precisa descobrir ANTES de estar com o paciente na frente.
 */
function SessaoDoSerproId({ sessaoAte }: { sessaoAte: string | null }) {
  const ativa = sessaoAte !== null && new Date(sessaoAte) > new Date();

  if (ativa) {
    return (
      <p className="text-sm text-foreground">
        {`Sessão de assinatura ativa até ${formatarDataEHora(sessaoAte)} — os documentos são assinados sem pedir nada.`}
      </p>
    );
  }

  return (
    <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
      <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
      <p className="text-sm text-foreground">
        A sessão de assinatura venceu. Você{' '}
        <strong className="font-medium">não consegue assinar documentos</strong> até autorizar de novo,
        logo abaixo.
      </p>
    </div>
  );
}

/**
 * O SerproID tem UMA etapa, e ela acontece fora daqui: o botão leva o médico ao
 * site do Serpro, ele aprova no app SerproID e o Serpro o devolve a
 * `SerproIdRetornoPage`. Não há espera ativa nem campo de senha.
 *
 * Quanto tempo a sessão dura é escolha dele LÁ (de 15 minutos a 7 dias, para
 * pessoa física) — a tela explica isso em vez de pedir um prazo que o Serpro
 * ignoraria.
 */
function SerproId({
  ocupado,
  renovando,
  documento,
  aoTrocarDocumento,
  aoAutorizar,
}: {
  ocupado: boolean;
  renovando: boolean;
  documento: string;
  aoTrocarDocumento: (valor: string) => void;
  aoAutorizar: () => void;
}) {
  const digitos = documento.replace(/\D/g, '').length;
  // Opcional: em branco é válido. Preenchido, tem que ser CPF ou CNPJ inteiro.
  const documentoValido = digitos === 0 || digitos === 11 || digitos === 14;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <Smartphone className="w-5 h-5 shrink-0 text-brand" />
        <p className="text-xs text-muted-foreground">
          Você será levado ao site do Serpro, aprova o uso do certificado no app SerproID e volta para cá.
          É lá que você escolhe por quanto tempo a sessão vale — de 15 minutos a 7 dias. Enquanto ela
          durar, os documentos são assinados sem pedir senha nem nova aprovação.
        </p>
      </div>

      <Campo
        id="documento-do-serproid"
        rotulo="CPF ou CNPJ do titular (opcional)"
        dica="Ajuda o Serpro a escolher o certificado. Em branco, você escolhe na tela do Serpro."
      >
        <Input
          id="documento-do-serproid"
          className={CLASSE_CONTROLE}
          inputMode="numeric"
          maxLength={18}
          value={documento}
          onChange={(e) => aoTrocarDocumento(e.target.value)}
        />
      </Campo>

      <div className="flex justify-end">
        <Button type="button" className="gap-2" disabled={ocupado || !documentoValido} onClick={aoAutorizar}>
          {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
          {renovando ? 'Renovar a sessão no SerproID' : 'Autorizar no SerproID'}
        </Button>
      </div>
    </div>
  );
}

/**
 * O SafeID tem DUAS etapas, e a tela mostra uma de cada vez: primeiro o médico
 * autoriza a plataforma pelo app do celular (uma vez só), depois informa a
 * senha do certificado. Enquanto a aprovação não volta, não há o que preencher.
 */
function SafeId({
  autorizacao,
  ocupado,
  cpf,
  aoTrocarCpf,
  senha,
  aoTrocarSenha,
  guardarSenha,
  aoEscolherModo,
  aoAutorizar,
  aoConcluir,
}: {
  autorizacao: MeResponse['perfil']['assinatura']['autorizacaoSafeId'];
  ocupado: boolean;
  cpf: string;
  aoTrocarCpf: (valor: string) => void;
  senha: string;
  aoTrocarSenha: (valor: string) => void;
  guardarSenha: boolean;
  aoEscolherModo: (valor: boolean) => void;
  aoAutorizar: () => void;
  aoConcluir: () => void;
}) {
  if (autorizacao?.situacao === MeAutorizacaoSafeIdSituacao.AGUARDANDO) {
    return (
      <div className="flex gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <Smartphone className="w-5 h-5 shrink-0 text-brand" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Esperando sua aprovação no app SafeID
          </p>
          <p className="text-xs text-muted-foreground">
            Abra o app SafeID no celular e autorize o uso do certificado pelo Nohra Clinic. Assim que
            aprovar, esta tela segue sozinha para o próximo passo — é a única vez que o celular entra.
          </p>
        </div>
      </div>
    );
  }

  if (autorizacao?.situacao === MeAutorizacaoSafeIdSituacao.AUTORIZADA) {
    return (
      <div className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-emerald-600/40 bg-emerald-600/10 p-4">
          <ShieldCheck className="w-5 h-5 shrink-0 text-emerald-600" />
          <p className="text-sm text-foreground">
            Autorização aprovada. Informe a senha do seu certificado para concluir.
          </p>
        </div>

        <Campo id="senha-do-safeid" rotulo="Senha do certificado SafeID">
          <InputSenha
            id="senha-do-safeid"
            className={CLASSE_CONTROLE}
            autoComplete="off"
            value={senha}
            onChange={(e) => aoTrocarSenha(e.target.value)}
          />
        </Campo>

        <ModoDeSenha valor={guardarSenha} aoEscolher={aoEscolherModo} />

        <div className="flex justify-end">
          <Button type="button" className="gap-2" disabled={ocupado || !senha} onClick={aoConcluir}>
            {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Concluir
          </Button>
        </div>
      </div>
    );
  }

  const recusada = autorizacao?.situacao === MeAutorizacaoSafeIdSituacao.RECUSADA;
  const expirada = autorizacao?.situacao === MeAutorizacaoSafeIdSituacao.EXPIRADA;

  return (
    <div className="space-y-4">
      {(recusada || expirada) && (
        <div className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
          <p className="text-sm text-foreground">
            {recusada
              ? 'A autorização foi recusada no app SafeID. Peça de novo quando quiser.'
              : // Vale para os dois prazos: o de aprovar no app e o de concluir
                // depois de aprovado (a Safeweb dá 7 dias).
                'A autorização do app SafeID expirou. Peça de novo.'}
          </p>
        </div>
      )}

      <Campo
        id="cpf-do-safeid"
        rotulo="CPF ou CNPJ do titular do certificado"
        dica="É o documento em que o certificado SafeID foi emitido — o mesmo que aparece no app. Pode ser o seu CPF ou o CNPJ da clínica."
      >
        <Input
          id="cpf-do-safeid"
          className={CLASSE_CONTROLE}
          inputMode="numeric"
          maxLength={18}
          value={cpf}
          onChange={(e) => aoTrocarCpf(e.target.value)}
        />
      </Campo>

      <div className="flex justify-end">
        <Button
          type="button"
          className="gap-2"
          // 11 (CPF) ou 14 (CNPJ): o SafeID emite nos dois, e o certificado da
          // clínica costuma estar no CNPJ.
          disabled={ocupado || ![11, 14].includes(cpf.replace(/\D/g, '').length)}
          onClick={aoAutorizar}
        >
          {ocupado ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
          Autorizar no app SafeID
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Você aprova no celular UMA vez. Depois disso, cada documento é assinado aqui mesmo, sem o app —
        com a senha do certificado, guardada ou digitada na hora, como você escolher.
      </p>
    </div>
  );
}

function ModoDeSenha({ valor, aoEscolher }: { valor: boolean; aoEscolher: (v: boolean) => void }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-brand mb-2">Ao assinar um documento</legend>
      {MODOS_DE_SENHA.map((modo) => (
        <label
          key={String(modo.guardar)}
          className={cn(
            'flex gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
            valor === modo.guardar ? 'border-brand bg-brand/5' : 'border-border hover:border-brand/40',
          )}
        >
          <input
            type="radio"
            name="modo-da-senha-do-certificado"
            className="mt-1 accent-brand"
            checked={valor === modo.guardar}
            onChange={() => aoEscolher(modo.guardar)}
          />
          <span className="space-y-0.5">
            <span className="block text-sm font-medium text-foreground">{modo.titulo}</span>
            <span className="block text-xs text-muted-foreground">{modo.descricao}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * ⚠️ `timeZone: 'UTC'`. O `notAfter` é um instante UTC exato; no fuso do Brasil,
 * `01/01/2099 00:00Z` sairia como 31/12/2098.
 */
function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

/**
 * A sessão do SerproID é um instante de relógio, ao contrário do `notAfter` do
 * certificado: aqui vale a hora de quem olha a tela, no fuso dele.
 */
function formatarDataEHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
